/* ============================================================
   TIMELESS — global theme behaviour
   Vanilla JS, no dependencies. Handles: cart drawer open/close,
   AJAX add-to-cart, line-item updates, mobile nav, variant
   selection on product pages.
   ============================================================ */
(function () {
  'use strict';

  const money = (cents) =>
    (window.Shopify && Shopify.currency)
      ? formatMoney(cents)
      : '$' + (cents / 100).toFixed(2);

  // Uses the shop's money format if present, else a simple fallback.
  function formatMoney(cents) {
    const fmt = (window.theme && theme.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/, value);
  }

  /* ---------- CART DRAWER ---------- */
  const drawer = document.querySelector('[data-cart-drawer]');

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); refreshCart().then(openDrawer); }
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); closeDrawer(); }
    if (e.target.matches('[data-cart-overlay]')) closeDrawer();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  /* ---------- ADD TO CART (AJAX) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[data-add-btn]');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: form.querySelector('[name="id"]').value, quantity: 1 }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await refreshCart();
      openDrawer();
      if (btn) btn.textContent = 'Added';
      setTimeout(() => { if (btn) { btn.textContent = original; btn.disabled = false; } }, 1200);
    } catch (err) {
      console.error(err);
      if (btn) { btn.textContent = 'Try again'; btn.disabled = false; }
    }
  });

  /* ---------- QUICK ADD (product cards) ---------- */
  document.addEventListener('click', async (e) => {
    const quick = e.target.closest('[data-quick-add]');
    if (!quick) return;
    e.preventDefault();
    const id = quick.getAttribute('data-quick-add');
    if (!id) return;
    quick.textContent = '…';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id, quantity: 1 }),
      });
      if (!res.ok) throw new Error();
      await refreshCart();
      openDrawer();
      quick.textContent = '+';
    } catch { quick.textContent = '!'; setTimeout(() => quick.textContent = '+', 1000); }
  });

  /* ---------- LINE ITEM QTY / REMOVE ---------- */
  document.addEventListener('click', async (e) => {
    const ctl = e.target.closest('[data-line-change]');
    if (!ctl) return;
    e.preventDefault();
    const key = ctl.getAttribute('data-key');
    const qty = parseInt(ctl.getAttribute('data-line-change'), 10);
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    });
    await refreshCart();
  });

  /* ---------- REFRESH CART CONTENTS ---------- */
  async function refreshCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      renderCart(cart);
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = '(' + cart.item_count + ')'; });
    } catch (err) { console.error(err); }
  }

  function renderCart(cart) {
    const itemsEl = drawer && drawer.querySelector('[data-cart-items]');
    const subtotalEl = drawer && drawer.querySelector('[data-cart-subtotal]');
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '<div class="cart-empty">Your cart is empty</div>';
      if (subtotalEl) subtotalEl.textContent = money(0);
      return;
    }

    itemsEl.innerHTML = cart.items.map((item) => `
      <div class="cart-line">
        <div class="cart-line__media">${item.image ? `<img src="${item.image.replace(/(\.[^.]*)$/, '_160x$1')}" alt="${escapeHtml(item.product_title)}">` : ''}</div>
        <div>
          <div class="cart-line__name">${escapeHtml(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-line__meta">${escapeHtml(item.variant_title)}</div>` : ''}
          <div class="cart-line__meta">${money(item.final_price)}</div>
          <div class="cart-line__meta" style="margin-top:8px;display:flex;gap:12px;align-items:center;">
            <button data-line-change="${item.quantity - 1}" data-key="${item.key}" aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button data-line-change="${item.quantity + 1}" data-key="${item.key}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button data-line-change="0" data-key="${item.key}" aria-label="Remove" style="font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--color-fade);">Remove</button>
      </div>
    `).join('');

    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* ---------- MOBILE MENU ---------- */
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const navToggle = document.querySelector('[data-nav-toggle]');

  function openMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.add('is-open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    if (navToggle) navToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }
  function closeMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    if (navToggle) navToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-nav-toggle]')) { e.preventDefault(); openMenu(); }
    if (e.target.closest('[data-mobile-close]')) { e.preventDefault(); closeMenu(); }
    // Mobile accordion groups — toggle the tapped group open/closed.
    const groupToggle = e.target.closest('[data-mobile-group-toggle]');
    if (groupToggle) {
      e.preventDefault();
      e.stopPropagation();
      const group = groupToggle.closest('[data-mobile-group]');
      if (!group) return;
      const isOpen = group.getAttribute('aria-expanded') === 'true';
      group.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      groupToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      const sub = group.querySelector('.mobile-menu__sub');
      if (sub) {
        if (isOpen) {
          sub.style.maxHeight = null;
        } else {
          sub.style.maxHeight = sub.scrollHeight + 'px';
        }
      }
    }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });

  /* ---------- PRODUCT GALLERY (thumb → main swap) ---------- */
  document.querySelectorAll('[data-thumbs]').forEach((rail) => {
    const stage = rail.parentElement.querySelector('.product__stage');
    if (!stage) return;
    const mains = stage.querySelectorAll('[data-main]');
    rail.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = thumb.getAttribute('data-thumb');
        rail.querySelectorAll('[data-thumb]').forEach((t) => {
          t.classList.toggle('is-active', t === thumb);
          t.setAttribute('aria-pressed', t === thumb ? 'true' : 'false');
        });
        mains.forEach((m) => m.classList.toggle('is-active', m.getAttribute('data-main') === idx));
      });
    });
  });

  /* ---------- VARIANT PICKER (product page) ---------- */
  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    const productData = JSON.parse(picker.getAttribute('data-variants') || '[]');
    const form = picker.closest('[data-product-form]') || document.querySelector('[data-product-form]');
    const idInput = form && form.querySelector('[name="id"]');
    const priceEl = document.querySelector('[data-product-price]');
    const selected = {};

    // Seed current selection from whatever is pre-pressed in the markup,
    // so a match exists before the shopper touches anything.
    picker.querySelectorAll('[data-option][aria-pressed="true"]').forEach((s) => {
      selected[s.getAttribute('data-option')] = s.getAttribute('data-value');
    });

    function updateSelection(group, btn) {
      const opt = btn.getAttribute('data-option');
      const val = btn.getAttribute('data-value');
      selected[opt] = val;

      picker.querySelectorAll(`[data-option="${opt}"]`).forEach((s) => s.setAttribute('aria-pressed', 'false'));
      btn.setAttribute('aria-pressed', 'true');

      // Update the "— Value" label shown beside swatch option names.
      const label = group.querySelector('[data-selected-value]');
      if (label) label.textContent = '— ' + val;

      // Match the variant whose option set equals the current selection.
      const match = productData.find((v) =>
        v.options.every((o, i) => selected['option' + (i + 1)] === o)
      );
      if (match && idInput) {
        idInput.value = match.id;
        if (priceEl) priceEl.textContent = money(match.price);

        // If the variant has an associated image, jump the gallery to it.
        if (match.featured_media && match.featured_media.position) {
          const targetIdx = String(match.featured_media.position - 1);
          const rail = document.querySelector('[data-thumbs]');
          const stage = document.querySelector('.product__stage');
          if (stage) {
            stage.querySelectorAll('[data-main]').forEach((m) =>
              m.classList.toggle('is-active', m.getAttribute('data-main') === targetIdx));
          }
          if (rail) {
            rail.querySelectorAll('[data-thumb]').forEach((t) => {
              const on = t.getAttribute('data-thumb') === targetIdx;
              t.classList.toggle('is-active', on);
              t.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
          }
        }
      }
    }

    picker.querySelectorAll('[data-option]').forEach((btn) => {
      const group = btn.closest('.variant-group');
      btn.addEventListener('click', () => updateSelection(group, btn));
    });
  });

  /* ---------- INIT ---------- */
  refreshCart();
})();
